import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalMatListComponent } from '../modal-mat-list/modal-mat-list.component';
import * as d3 from 'd3';

interface Relationship {
  source: string; // RUT del nodo origen
  target: string; // RUT del nodo destino
  label: string;  // Tipo de relación (ej: 'OWNED_BY', 'REPRESENTED_BY')
  information_source: any; // Información adicional de la relación
  sourceName: string; // Nombre completo o razón social del source
  targetName: string; // Nombre completo o razón social del target
  sourceDV?: string; // Dígito verificador del source
  targetDV?: string; // Dígito verificador del target
}

@Component({
  selector: 'app-radial-org-chart',
  templateUrl: './radial-org-chart.component.html',
  styleUrls: ['./radial-org-chart.component.scss']
})
export class RadialOrgChartComponent {
  @Input() data: Relationship[] = [];
  @Input() idPadre: string = '';
  @Input() width: number | undefined = undefined;
  @Input() height: number | undefined = undefined;
  @ViewChild('graphContainer') containerRef!: ElementRef;

  private nodes: any[] = [];
  private links: any[] = [];
  
  private svgElement!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private zoomBehavior!: d3.ZoomBehavior<SVGSVGElement, unknown>;
  private currentTransform: d3.ZoomTransform = d3.zoomIdentity;

  constructor(private dialog: MatDialog) {}

  ngAfterViewInit(): void {
    // Espera un ciclo de detección para que el @Input() se reciba
    setTimeout(() => {
      if (!this.data || this.data.length === 0) {
        console.error('No data provided for the radial org chart');
        return;
      }

      this.data.sort((a, b) => {
        // ordernar de mayor a menor numero de relaciones
        const aCount = this.data.filter(rel => rel.source === a.source || rel.target === a.source).length;
        const bCount = this.data.filter(rel => rel.source === b.source || rel.target === b.source).length;
        if (aCount < bCount) return -1;
        if (aCount > bCount) return 1;
        return 0;
      });

      // Convertir data a nodos y enlaces
      const nodeMap = new Map<string, any>();

      this.data.forEach(rel => {
        // Agrega o actualiza el nodo source
        if (!nodeMap.has(rel.source)) {
          nodeMap.set(rel.source, {
            id: rel.source,
            name: rel.sourceName,
            labels: [rel.label],
            rut: rel.source && (rel.sourceDV || rel.sourceDV == '0') ? (rel.source + '-' + rel.sourceDV) : ''
          });
        } else {
          const existing = nodeMap.get(rel.source);
          existing.labels.push(rel.label);
        }

        // Agrega o actualiza el nodo target
        if (!nodeMap.has(rel.target)) {
          nodeMap.set(rel.target, {
            id: rel.target,
            name: rel.targetName,
            labels: [rel.label],
            rut: rel.target && (rel.targetDV || rel.targetDV == '0') ? (rel.target + '-' + rel.targetDV) : ''
          });
        } else {
          const existing = nodeMap.get(rel.target);
          existing.labels.push(rel.label);
        }
      });

      this.nodes = Array.from(nodeMap.values());

      this.links = this.data.map(d => ({
        source: d.source,
        target: d.target,
        information_source: d.information_source,// puede venir shares_percent
      }));

      d3.select(this.containerRef.nativeElement).select('svg').remove();
      this.createGraph(this.nodes, this.links);

      window.addEventListener('nodoClick', (event: any) => {
        const id = event.detail;
        const element = this.nodes.find((node: any) => node.id == id);
        if (element) {
          this.handleNodoClick(element);
        } else {
          console.warn(`Nodo con id ${id} no encontrado`);
        }
      });
    });
  }

  handleNodoClick(node: any) {
    this.dialog.open(ModalMatListComponent, {
      data: {
        title: node.name || '',
        list: [
          { label: 'Tipo: ', value: `${this.isRutEmpresa(node.id) ? 'Empresa' : 'Persona'}` },
          { label: 'Rut: ', value: node.rut }
        ]
      }
    });
  }

  handleLinkClick(link: any) {
    const source = link.source.id;
    const target = link.target.id;
    const linkOrginal = this.links.find(l => l.source.id === source && l.target.id === target); // se busca el original porque d3 modifica los objetos
    if(linkOrginal) {
      const list: any[] = [];
      if (link.information_source.source) {
        list.push({ label: 'Fuente: ', value: linkOrginal.information_source.source });
      }
      if (linkOrginal.information_source.published_at) {
        list.push({ label: 'Publicación: ', value: linkOrginal.information_source.published_at });
      }
      if (linkOrginal.information_source.shares_percent) {
        list.push({ label: 'Porcentaje: ', value: `${linkOrginal.information_source.shares_percent}%` });
      }
      if (linkOrginal.information_source.started_at) {
        list.push({ label: 'Desde: ', value: linkOrginal.information_source.started_at });
      }
      this.dialog.open(ModalMatListComponent, {
        data: {
          title: `Conexión ${this.formatearRUT(linkOrginal.source.rut)} - ${this.formatearRUT(linkOrginal.target.rut)}` || '',
          list: list
        }
      });
    }
  }

  formatearRUT(rut: string) : string {
    let rutFormateado = '';
    // Agregar puntos al RUT
    if(rut && rut !== '' && rut.includes('-')){
      const rut1 = rut.split('-')[0];
      const dv = rut.split('-')[1];
      rutFormateado = rut1?.replace(/^(\d{1,3})(\d{3})(\d{3})$/, '$1.$2.$3') || '';
      // Agregar el dígito verificador al RUT formateado
      rutFormateado += "-" + (dv || '');
    }

    return rutFormateado ? rutFormateado : rut || '';
  }

  zoomIn() {
    const newScale = this.currentTransform.k * 1.2;
    this.svgElement.transition().call(this.zoomBehavior.scaleTo, newScale);
  }

  zoomOut() {
    const newScale = this.currentTransform.k / 1.2;
    this.svgElement.transition().call(this.zoomBehavior.scaleTo, newScale);
  }

  isRutEmpresa(rut: string): boolean {
    return Number(rut) > 50000000 ? true : false;
  }

  createGraph(nodes: any[], links: any[]) {
    // Contenedor y sus dimensiones
    const container = this.containerRef.nativeElement;
    const width = this.width || container.offsetWidth;
    const height = this.height || container.offsetHeight;

    // SVG dentro del contenedor
    this.svgElement = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    // Contenedor de zoom
    const zoomContainer = this.svgElement.append('g');

    // Zoom con scroll
    this.zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.01, 3])
      .on('zoom', (event) => {
        this.currentTransform = event.transform;
        zoomContainer.attr('transform', event.transform);
      });
    
    this.svgElement.call(this.zoomBehavior);

    // Simulación con fuerzas físicas
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id((d: any) => d.id).distance(nodes.length > 20 ? 700 : 400)) // enlaces entre nodos
      .force('charge', d3.forceManyBody().strength(-800)) // repulsión entre nodos
      .force('center', d3.forceCenter(width / 2, height / 2)) // centrado del grafo
      .force('collide', d3.forceCollide().radius(180)); // colisión entre nodos

    // Flecha dentro de <defs>
    this.svgElement.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 100) // distancia desde el final de la línea (según tamaño del nodo)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('xoverflow', 'visible')
      .append('svg:path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#999')
      .style('stroke', 'none');

    // Líneas de los enlaces
    const link = zoomContainer.append('g')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.8)
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '5,5') // línea discontinua
      .attr('marker-end', 'url(#arrowhead)') // flecha al final
      /*.on('click', (event: any, d: any) => {
        this.handleLinkClick(d);
      });*/

    // Capa invisible más ancha para capturar clics
    const linkHitbox = zoomContainer.append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 20)
      .style('cursor', 'pointer')
      .on('click', (event: any, d: any) => {
        this.handleLinkClick(d);
      });

    let hoverTimeout: any = null;
    linkHitbox
      .on('mouseover', function (event: any, d: any) {
        hoverTimeout = setTimeout(() => {
          // Resalta solo el link actual
          link.attr('stroke-opacity', (l: any) => (l === d ? 1 : 0.05));
          link.attr('stroke-width', (l: any) => (l === d ? 3 : 1));

          // Baja opacidad de nodos que no están conectados
          node.style('opacity', (n: any) =>
            (n.id === d.source.id || n.id === d.target.id) ? 1 : 0.1
          );

          // También podrías aplicar esto a labels si deseas
          linkLabels.style('opacity', (l: any) => (l === d ? 1 : 0.1));
        }, 1000); // 2 segundos
      })
      .on('mouseout', function () {
        clearTimeout(hoverTimeout);

        // Restaurar opacidades
        link.attr('stroke-opacity', 0.8).attr('stroke-width', 1.5);
        node.style('opacity', 1);
        linkLabels.style('opacity', 1);
      });

    // Etiquetas sobre los enlaces (shares_percent)
    const linkLabels = zoomContainer.append('g')
      .selectAll('text')
      .data(links)
      .enter()
      .append('text')
      .attr('font-size', '11px')
      .attr('fill', '#333')
      .attr('text-anchor', 'middle')
      .text((d: any) => {
        if (d.information_source?.shares_percent && d.information_source?.shares_percent != null) {
          return `${d.information_source.shares_percent}%`;
        }
        return ''; // si no hay porcentaje, no se muestra nada
      });

    // Nodos
    const node = zoomContainer.append('g')
      .selectAll('foreignObject')
      .data(nodes)
      .enter()
      .append('foreignObject')
      .attr('width', 150)
      .attr('height', 200)
      .attr('x', (d: any) => d.x)
      .attr('y', (d: any) => d.y)
      //border: 1px solid ${this.idPadre == d.id ? '#339BED' : (this.isRutEmpresa(d.id) ? '#216487' : '#00A894')};
      //<div style="margin-bottom: 2%"><strong>${d.id}</strong></div>
      .html((d: any, i) => `
      <div xmlns="http://www.w3.org/1999/xhtml" style="
          border-radius: 40px;
          background-color: #FFFFFF;
          padding: 8px;
          font-family: Arial;
          font-size: 12px;
          text-align: center;
          color: ${this.idPadre == d.id ? '#001F25' : (this.isRutEmpresa(d.id) ? '#001E2E' : '#001F25')};
        ">
          <div 
            style="
              margin-bottom: 2%; 
              font-size: 40px;
              color: ${this.idPadre == d.id ? '#339BED' : (this.isRutEmpresa(d.id) ? '#216487' : '#00A894')};"
            ><strong>${this.isRutEmpresa(d.id) ? '<i class="fa fa-building-o" aria-hidden="true"></i>' : '<i class="fa fa-user-o" aria-hidden="true"></i>'}</strong></div>
          <div style="margin-bottom: 2%"><strong>${d.name}</strong></div>
          <div style="margin-top: 6px;">
            <button
              onclick="window.dispatchEvent(new CustomEvent('nodoClick', { detail: '${d.id}' }))"
              style="
                background-color: #009688;
                color: white;
                border: none;
                padding: 4px 12px;
                font-size: 13px;
                border-radius: 16px;
                display: inline-block;
                cursor: pointer;
              "
            >
              Ver datos
            </button>
          </div>

        </div>
      `);

    // Arrastre de los nodos
    node.call(
      d3.drag<SVGForeignObjectElement, any>()
        .on('start', dragStarted)
        .on('drag', dragged)
        .on('end', dragEnded)
    );

    node
    .on('mouseover', function (event: any, d: any) {
      // Nodos conectados (source o target)
      const connectedNodeIds = new Set<string>();
      links.forEach((l: any) => {
        if (l.source.id === d.id) connectedNodeIds.add(l.target.id);
        if (l.target.id === d.id) connectedNodeIds.add(l.source.id);
      });

      // Resaltar nodo actual y los conectados
      node.style('opacity', (n: any) =>
        n.id === d.id || connectedNodeIds.has(n.id) ? 1 : 0.1
      );

      // Resaltar links relacionados
      link.attr('stroke-opacity', (l: any) =>
        l.source.id === d.id || l.target.id === d.id ? 1 : 0.05
      );
      link.attr('stroke-width', (l: any) =>
        l.source.id === d.id || l.target.id === d.id ? 3 : 1
      );

      // Resaltar etiquetas
      linkLabels.style('opacity', (l: any) =>
        l.source.id === d.id || l.target.id === d.id ? 1 : 0.1
      );
    })
    .on('mouseout', function () {
      node.style('opacity', 1);
      link.attr('stroke-opacity', 0.8).attr('stroke-width', 1.5);
      linkLabels.style('opacity', 1);
    });

    // En cada "tick" de la simulación actualizamos posiciones
    simulation.on('tick', () => {
    // Actualizamos las líneas visibles (con flecha)
    link
      .attr('x1', (d: any) => {
        const dx = d.target.x - d.source.x;
        const dy = d.target.y - d.source.y;
        const angle = Math.atan2(dy, dx);
        const nodeWidth = d.source.width || 150;
        const offsetX = Math.cos(angle) * (nodeWidth / 10);
        return d.source.x + offsetX;
      })
      .attr('y1', (d: any) => {
        const dx = d.target.x - d.source.x;
        const dy = d.target.y - d.source.y;
        const angle = Math.atan2(dy, dx);
        const nodeHeight = d.source.height || 200;
        const offsetY = Math.sin(angle) * (nodeHeight / 10);
        return d.source.y + offsetY;
      })
      .attr('x2', (d: any) => {
        const dx = d.source.x - d.target.x;
        const dy = d.source.y - d.target.y;
        const angle = Math.atan2(dy, dx);
        const nodeWidth = d.source.width || 150;
        const offsetX = Math.cos(angle) * (nodeWidth / 10);
        return d.target.x + offsetX;
      })
      .attr('y2', (d: any) => {
        const dx = d.source.x - d.target.x;
        const dy = d.source.y - d.target.y;
        const angle = Math.atan2(dy, dx);
        const nodeHeight = d.source.height || 200;
        const offsetY = Math.sin(angle) * (nodeHeight / 10);
        return d.target.y + offsetY;
      });

    // Actualiza la línea invisible exactamente igual
    linkHitbox
      .attr('x1', (d: any) => {
        const dx = d.target.x - d.source.x;
        const dy = d.target.y - d.source.y;
        const angle = Math.atan2(dy, dx);
        const nodeWidth = d.source.width || 150;
        const offsetX = Math.cos(angle) * (nodeWidth / 10);
        return d.source.x + offsetX;
      })
      .attr('y1', (d: any) => {
        const dx = d.target.x - d.source.x;
        const dy = d.target.y - d.source.y;
        const angle = Math.atan2(dy, dx);
        const nodeHeight = d.source.height || 200;
        const offsetY = Math.sin(angle) * (nodeHeight / 10);
        return d.source.y + offsetY;
      })
      .attr('x2', (d: any) => {
        const dx = d.source.x - d.target.x;
        const dy = d.source.y - d.target.y;
        const angle = Math.atan2(dy, dx);
        const nodeWidth = d.source.width || 150;
        const offsetX = Math.cos(angle) * (nodeWidth / 10);
        return d.target.x + offsetX;
      })
      .attr('y2', (d: any) => {
        const dx = d.source.x - d.target.x;
        const dy = d.source.y - d.target.y;
        const angle = Math.atan2(dy, dx);
        const nodeHeight = d.source.height || 200;
        const offsetY = Math.sin(angle) * (nodeHeight / 10);
        return d.target.y + offsetY;
      });

    // Actualizamos las posiciones de los nodos
    node
      .attr('x', (d: any) => d.x - 75)
      .attr('y', (d: any) => d.y - 40);

    // Actualizamos las etiquetas de los enlaces (porcentaje)
    linkLabels
      .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
      .attr('y', (d: any) => (d.source.y + d.target.y) / 2 - 5);
    });

    // Función que se ejecuta cuando se empieza a arrastrar un nodo
    function dragStarted(event: d3.D3DragEvent<SVGForeignObjectElement, any, any>, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    // Mientras se arrastra el nodo
    function dragged(event: d3.D3DragEvent<SVGForeignObjectElement, any, any>, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }

    // Cuando termina el arrastre
    function dragEnded(event: d3.D3DragEvent<SVGForeignObjectElement, any, any>, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }
  }
  
}
